/* eslint-disable import/prefer-default-export */
// https://github.com/google/closure-library/blob/master/closure/goog/date/date.js#L570
import dayjs from 'dayjs'
import { TimeZoneStrings } from 'enums/TimeZone'
import i18n from 'i18n'
import { getOffset, getShortDateDisplay, getYearDateDisplay } from './timeZoneUtil'

/* transform interval object to milliseconds
  eg.
  {
      "years": 0,
      "months": 0,
      "days": 1,
      "hours": 0,
      "minutes": 0,
      "seconds": 0
  }
*/

export interface TimeUnitConvert {
  SECONDS_TO_MIN: number
  MINS_TO_HOUR: number
  HOURS_TO_DAY: number
  DAYS_TO_MONTH: number
  DAYS_TO_YEAR: number
  COPYRIGHT_YEAR: number
  MS_IN_MIN: number
  MS_IN_HOUR: number
  MS_IN_DAY: number
  MS_IN_WEEK: number
  MS_IN_MONTH: number
  MS_IN_YEAR: number
}

export const date: TimeUnitConvert = {
  SECONDS_TO_MIN: 60,
  MINS_TO_HOUR: 60,
  HOURS_TO_DAY: 24,
  DAYS_TO_MONTH: 30,
  DAYS_TO_YEAR: 365,
  COPYRIGHT_YEAR: new Date().getFullYear(),
  MS_IN_MIN: 0,
  MS_IN_HOUR: 0,
  MS_IN_DAY: 0,
  MS_IN_WEEK: 0,
  MS_IN_MONTH: 0,
  MS_IN_YEAR: 0
}
date.MS_IN_MIN = date.SECONDS_TO_MIN * 1000
date.MS_IN_HOUR = date.MS_IN_MIN * date.MINS_TO_HOUR
date.MS_IN_DAY = date.MS_IN_HOUR * date.HOURS_TO_DAY
date.MS_IN_WEEK = date.MS_IN_DAY * 7
date.MS_IN_MONTH = date.MS_IN_DAY * date.DAYS_TO_MONTH
date.MS_IN_YEAR = date.MS_IN_DAY * date.DAYS_TO_YEAR

export interface IntervalObj {
  years: number
  months: number
  days: number
  hours: number
  minutes: number
  seconds: number
}

export function getIntervalObj(
  opt_years = 0,
  opt_months = 0,
  opt_days = 0,
  opt_hours = 0,
  opt_minutes = 0,
  opt_seconds = 0
): IntervalObj {
  return {
    years: opt_years,
    months: opt_months,
    days: opt_days,
    hours: opt_hours,
    minutes: opt_minutes,
    seconds: opt_seconds
  }
}

export function addUserTzInterval(
  time: Date,
  interval: IntervalObj,
  userSettingTimeZone?: TimeZoneStrings
) {
  const result = new Date(time.getTime())
  const initLocalOffset = result.getTimezoneOffset()
  const initTzOffset = getOffset(result, userSettingTimeZone)
  // adjust local time to match user setting time
  result.setTime(result.getTime() + (initLocalOffset - initTzOffset) * 60 * 1000)

  // add local tz DST change caused by adjustment
  let afterLocalOffset = result.getTimezoneOffset()
  result.setTime(result.getTime() + (afterLocalOffset - initLocalOffset) * 60 * 1000)
  Object.entries(interval).forEach((entry) => {
    const unit = entry[0].slice(0, -1) // 'months' -> 'month'
    const count = entry[1] // unit count
    result.setTime(dayjs(result.getTime()).add(count, unit).valueOf())
  })

  // substract local tz DST change (caused by adjustment and add interval)
  afterLocalOffset = result.getTimezoneOffset()
  result.setTime(result.getTime() - (afterLocalOffset - initTzOffset) * 60 * 1000)

  // add user tz DST change
  const afterTzOffset = getOffset(result, userSettingTimeZone)
  result.setTime(result.getTime() + (afterTzOffset - initTzOffset) * 60 * 1000)

  return result
}

export const getRelativeTimeString = (
  millisecond: string | number | undefined,
  timeZone: TimeZoneStrings
) => {
  if (!millisecond) {
    return ''
  }

  const time = Number(millisecond)
  let seconds = (new Date().getTime() - time) / 1000
  const minutes = Math.floor(seconds / date.SECONDS_TO_MIN)
  const hours = Math.floor(minutes / date.MINS_TO_HOUR)
  seconds = Math.floor(seconds) <= 0 ? 1 : Math.floor(seconds)

  // NI-2606: i18n.t() is called synchronously here outside the React lifecycle
  // (see timestampResolver.ts). If translations haven't finished loading from the
  // HTTP backend, i18n.t() returns the raw key (e.g. "timeRelativeAgo.minutes")
  // instead of the formatted value. Guard with isInitialized and fall back to
  // the plain format used by the en translation.
  const formatRelative = (key: 'seconds' | 'minutes' | 'hours', value: number) => {
    if (i18n.isInitialized) {
      const translated = i18n.t(`timeRelativeAgo.${key}`, { time: value })
      if (translated && translated !== `timeRelativeAgo.${key}`) {
        return translated
      }
    }
    return `${value}${key.charAt(0)}`
  }

  if (seconds < date.SECONDS_TO_MIN) {
    return formatRelative('seconds', seconds)
  }
  if (minutes < date.MINS_TO_HOUR) {
    return formatRelative('minutes', minutes)
  }
  if (hours < date.HOURS_TO_DAY) {
    return formatRelative('hours', hours)
  }

  const fullYear = new Date(time).getFullYear()
  const currentYear = new Date().getFullYear()
  if (fullYear === currentYear) {
    return getShortDateDisplay(time, timeZone)
  }

  return getYearDateDisplay(time, timeZone)
}
